import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  visible?: boolean;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
  className?: string;
  contentClassName?: string;
  trigger?: 'hover' | 'click';
  usePortal?: boolean;
}

type Placement = NonNullable<TooltipProps['placement']>;

const TOOLTIP_Z_INDEX = 99999;
const MARGIN = 8;
const VIEWPORT_PAD = 8;

const getInlinePlacementClasses = (placement: Placement) => {
  switch (placement) {
    case 'bottom':
      return 'top-full left-1/2 -translate-x-1/2 mt-2';
    case 'left':
      return 'right-full top-1/2 -translate-y-1/2 mr-2';
    case 'right':
      return 'left-full top-1/2 -translate-y-1/2 ml-2';
    case 'topLeft':
      return 'bottom-full left-0 mb-2';
    case 'topRight':
      return 'bottom-full right-0 mb-2';
    case 'bottomLeft':
      return 'top-full left-0 mt-2';
    case 'bottomRight':
      return 'top-full right-0 mt-2';
    case 'top':
    default:
      return 'bottom-full left-1/2 -translate-x-1/2 mb-2';
  }
};

const getArrowClasses = (placement: Placement) => {
  switch (placement) {
    case 'bottom':
    case 'bottomLeft':
    case 'bottomRight':
      return '-top-1 left-1/2 -translate-x-1/2';
    case 'left':
      return '-right-1 top-1/2 -translate-y-1/2';
    case 'right':
      return '-left-1 top-1/2 -translate-y-1/2';
    case 'top':
    case 'topLeft':
    case 'topRight':
    default:
      return '-bottom-1 left-1/2 -translate-x-1/2';
  }
};

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  visible = false,
  placement = 'top',
  className = '',
  contentClassName = '',
  trigger = 'hover',
  usePortal = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; placement: Placement } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const showTooltip = visible || (trigger === 'hover' ? isHovered : isClicked);

  useEffect(() => {
    if (trigger !== 'click' || !isClicked) return;

    const handler = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsClicked(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [trigger, isClicked]);

  const measureAndPosition = () => {
    if (!rootRef.current || !tooltipRef.current) return;

    const triggerRect = rootRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const spaceAbove = triggerRect.top;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const needed = tooltipRect.height + MARGIN + VIEWPORT_PAD;

    let effectivePlacement: Placement = placement;
    if (
      (placement === 'top' || placement === 'topLeft' || placement === 'topRight') &&
      spaceAbove < needed &&
      spaceBelow > spaceAbove
    ) {
      effectivePlacement = placement === 'top' ? 'bottom' : placement === 'topLeft' ? 'bottomLeft' : 'bottomRight';
    } else if (
      (placement === 'bottom' || placement === 'bottomLeft' || placement === 'bottomRight') &&
      spaceBelow < needed &&
      spaceAbove > spaceBelow
    ) {
      effectivePlacement = placement === 'bottom' ? 'top' : placement === 'bottomLeft' ? 'topLeft' : 'topRight';
    }

    const base: Record<Placement, { top: number; left: number }> = {
      top: {
        top: triggerRect.top - tooltipRect.height - MARGIN,
        left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
      },
      topLeft: {
        top: triggerRect.top - tooltipRect.height - MARGIN,
        left: triggerRect.left,
      },
      topRight: {
        top: triggerRect.top - tooltipRect.height - MARGIN,
        left: triggerRect.right - tooltipRect.width,
      },
      bottom: {
        top: triggerRect.bottom + MARGIN,
        left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
      },
      bottomLeft: {
        top: triggerRect.bottom + MARGIN,
        left: triggerRect.left,
      },
      bottomRight: {
        top: triggerRect.bottom + MARGIN,
        left: triggerRect.right - tooltipRect.width,
      },
      left: {
        top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
        left: triggerRect.left - tooltipRect.width - MARGIN,
      },
      right: {
        top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
        left: triggerRect.right + MARGIN,
      },
    };

    const next = base[effectivePlacement];
    const top = Math.max(
      VIEWPORT_PAD,
      Math.min(next.top, window.innerHeight - tooltipRect.height - VIEWPORT_PAD)
    );
    const left = Math.max(
      VIEWPORT_PAD,
      Math.min(next.left, window.innerWidth - tooltipRect.width - VIEWPORT_PAD)
    );
    setCoords((prev) => {
      if (
        prev &&
        prev.placement === effectivePlacement &&
        prev.top === top &&
        prev.left === left
      ) {
        return prev;
      }
      return { placement: effectivePlacement, top, left };
    });
  };

  useLayoutEffect(() => {
    if (!showTooltip || !usePortal) return;
    measureAndPosition();
  }, [showTooltip, placement, content, usePortal]);

  useEffect(() => {
    if (!showTooltip || !usePortal) return;

    window.addEventListener('scroll', measureAndPosition, true);
    window.addEventListener('resize', measureAndPosition);
    return () => {
      window.removeEventListener('scroll', measureAndPosition, true);
      window.removeEventListener('resize', measureAndPosition);
    };
  }, [showTooltip, usePortal]);

  const tooltipContent = (
    <div className={`bg-hero-red text-white text-sm rounded-md p-3 shadow-lg min-w-[200px] max-w-xs relative ${contentClassName}`}>
      {content}
      <div className={`absolute w-2 h-2 bg-hero-red transform rotate-45 ${getArrowClasses(coords?.placement ?? placement)}`} />
    </div>
  );

  const tooltipNode =
    showTooltip && usePortal
      ? createPortal(
          <div
            ref={tooltipRef}
            className="fixed pointer-events-none"
            style={{
              zIndex: TOOLTIP_Z_INDEX,
              ...(coords ? { top: coords.top, left: coords.left } : { top: -9999, left: -9999 }),
            }}
          >
            {tooltipContent}
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={`relative inline-block ${className}`}
      onMouseEnter={trigger === 'hover' ? () => setIsHovered(true) : undefined}
      onMouseLeave={trigger === 'hover' ? () => setIsHovered(false) : undefined}
      onClick={
        trigger === 'click'
          ? (event) => {
              event.stopPropagation();
              setIsClicked((prev) => !prev);
            }
          : undefined
      }
    >
      {children}
      {tooltipNode}
      {showTooltip && !usePortal && (
        <div
          className={`absolute pointer-events-none ${getInlinePlacementClasses(placement)}`}
          style={{ zIndex: TOOLTIP_Z_INDEX }}
        >
          {tooltipContent}
        </div>
      )}
    </div>
  );
};
